import React, { useEffect, useState, useRef } from 'react';
import { XIcon, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from './Button';
interface TalkToUsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}
interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  organization: string;
  phone: string;
  coachCount: string;
  teacherCount: string;
  intent: string[];
}
const INITIAL_DATA: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  role: '',
  organization: '',
  phone: '',
  coachCount: '',
  teacherCount: '',
  intent: []
};
export const TalkToUsOverlay: React.FC<TalkToUsOverlayProps> = ({
  isOpen,
  onClose
}) => {
  const [formData, setFormData] = useState<FormData>(INITIAL_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Focus management
  const firstInputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Focus the first input after animation
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
    } else {
      document.body.style.overflow = 'unset';
      // Reset form after closing (optional, maybe keep state if they reopen?)
      // For now, let's keep state to avoid frustration if accidental close
    }
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleIntentToggle = (option: string) => {
    setFormData(prev => ({
      ...prev,
      intent: prev.intent.includes(option) ? prev.intent.filter(item => item !== option) : [...prev.intent, option]
    }));
  };
  const handleBlur = (e: React.FocusEvent<any>) => {
    const {
      name
    } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    // Build payload, omitting empty optional fields
    const payload: Record<string, unknown> = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      source: 'marketing-web'
    };
    if (formData.role) payload.role = formData.role;
    if (formData.organization) payload.organization = formData.organization;
    if (formData.coachCount) payload.teamSize = formData.coachCount;
    if (formData.teacherCount) payload.teacherCount = formData.teacherCount;
    if (formData.intent.length > 0) payload.goals = formData.intent;
    if (formData.phone) payload.metadata = {
      phone: formData.phone
    };
    try {
      const response = await fetch('https://app.nisa.coach/api/leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        setIsSuccess(true);
        setFormData(INITIAL_DATA);
        setTouched({});
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleClose = () => {
    if (isSuccess) {
      // Reset success state after closing if it was successful
      setTimeout(() => setIsSuccess(false), 300);
    }
    onClose();
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 bg-white overflow-y-auto animate-fade-in" role="dialog" aria-modal="true" aria-labelledby="talk-to-us-title" ref={overlayRef} data-id="element-359" data-mp="2evpaletf8oka">
      {/* Background gradient blobs — top half only */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-360" data-mp="2on1y5u86sski">
        <div className="absolute -top-[100px] -right-[150px] w-[800px] h-[700px] bg-gradient-to-br from-cyan-200/25 via-teal-200/21 to-cyan-200/14 rounded-[60%_40%_55%_45%] blur-3xl animate-float" data-id="element-361" data-mp="2tlmtpgchuo2m" />
        <div className="absolute -top-[50px] -left-[200px] w-[700px] h-[600px] bg-gradient-to-tr from-cyan-200/21 via-cyan-100/15 to-teal-200/13 rounded-[40%_60%_45%_55%] blur-3xl animate-float" style={{
        animationDelay: '2s'
      }} data-id="element-362" data-mp="32l9qfoua35pc" />
        <div className="absolute top-[5%] left-[25%] w-[600px] h-[500px] bg-gradient-to-r from-teal-200/15 via-cyan-100/13 to-cyan-200/15 rounded-[45%_55%_40%_60%] blur-3xl animate-float" style={{
        animationDelay: '3.5s'
      }} data-id="element-363" data-mp="189lbhmo1r34y" />
        {/* Fade-out mask so blobs don't bleed into lower form area */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent via-45% to-white" data-id="element-364" data-mp="10uj4zkwkl6x1" />
      </div>

      {/* Top Bar */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-md px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-365" data-mp="qz7mcri1jfk0">
        <div className="flex items-center" data-id="element-366" data-mp="1olojnwqrxt5r">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jP7ueorRtM9x7giMYpBsys/nisa-capriola-logotype.png" alt="Nisa" className="h-6 sm:h-8 w-auto" data-id="element-367" data-mp="1jnd89hktzalf" />
        </div>
        <button onClick={handleClose} className="text-gray-400 hover:text-gray-900 transition-colors p-2 rounded-lg hover:bg-gray-50" aria-label="Close overlay" data-id="element-368" data-mp="1eq3lyasqnf1t">
          <XIcon className="w-6 h-6" data-id="element-369" data-mp="39n2m6e2ce9o" />
        </button>
      </div>

      {/* Main Content */}
      <div className="min-h-[calc(100vh-64px)] flex flex-col items-center justify-center py-12 px-4 sm:px-6" data-id="element-370" data-mp="3vbl7sefyayqh">
        <div className="w-full max-w-[600px] animate-slide-up" data-id="element-371" data-mp="pqptpm8d10e">
          {isSuccess ? <div className="text-center py-12" data-id="element-372" data-mp="5nsa2dve76dg">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 text-green-600 mb-8 animate-scale-in" data-id="element-373" data-mp="5lfrpzlq2tzv">
                <CheckCircle2 className="w-10 h-10" data-id="element-374" data-mp="3rtxl0y9s6sot" />
              </div>
              <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-6" data-id="element-375" data-mp="3e3bzt2wuu6nr">
                Thanks for reaching out!
              </h2>
              <p className="text-lg text-gray-600 mb-10 max-w-md mx-auto leading-relaxed" data-id="element-376" data-mp="3iutzzor2037o">
                We'll follow up soon to learn more about your organization and
                goals. We'll also share how Nisa can support coaching
                consistency and strengthen impact.
              </p>
              <button onClick={handleClose} className="text-primary-600 font-medium hover:text-primary-700 hover:underline transition-all" data-id="element-377" data-mp="2v4zobaih0kp0">
                Back to Nisa
              </button>
            </div> : <>
              <div className="text-center mb-10 sm:mb-12" data-id="element-378" data-mp="2gi0ytl36c0cx">
                <h1 id="talk-to-us-title" className="font-serif text-3xl md:text-4xl text-cyan-950 mb-4 font-[500]" data-id="element-379" data-mp="2bjfupakswwj7">
                  Request a Nisa demo
                </h1>
                <p className="text-lg text-gray-600 max-w-xl mx-auto leading-relaxed" data-id="element-380" data-mp="2kjm5gop47ieo">
                  Get Nisa for your school, district, or curriculum provider.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8" data-id="element-381" data-mp="2ui2031ncp9sx">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-382" data-mp="388xbaklbo2zo">
                  <div className="space-y-1.5" data-id="element-383" data-mp="3885dr485zv6r">
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700" data-id="element-384" data-mp="346tpb2111g05">
                      First name
                    </label>
                    <input ref={firstInputRef} type="text" id="firstName" name="firstName" required value={formData.firstName} onChange={handleChange} onBlur={handleBlur} className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all text-gray-900 placeholder:text-gray-400" data-id="element-385" data-mp="3i509jfd1zhhc" />
                  </div>
                  <div className="space-y-1.5" data-id="element-386" data-mp="xdye3yzan9oh">
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700" data-id="element-387" data-mp="12dt3fy3f7r7f">
                      Last name
                    </label>
                    <input type="text" id="lastName" name="lastName" required value={formData.lastName} onChange={handleChange} onBlur={handleBlur} className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all text-gray-900 placeholder:text-gray-400" data-id="element-388" data-mp="9q6q3y4e587d" />
                  </div>
                </div>

                <div className="space-y-1.5" data-id="element-389" data-mp="3n53b9wasinvv">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700" data-id="element-390" data-mp="3n3s8nzt2zm6c">
                    Work email
                  </label>
                  <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} onBlur={handleBlur} className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all text-gray-900 placeholder:text-gray-400" data-id="element-391" data-mp="3amhftkj8lm5a" />
                </div>

                <div className="space-y-1.5" data-id="element-392" data-mp="2wo0yz0rnuf9f">
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700" data-id="element-393" data-mp="2rvg8o97yqkan">
                    Role
                  </label>
                  <div className="relative" data-id="element-394" data-mp="2mxenniqz8awj">
                    <select id="role" name="role" required value={formData.role} onChange={handleChange} onBlur={handleBlur} className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all text-gray-900 appearance-none cursor-pointer" data-id="element-395" data-mp="2hy2354nkvfx4">
                      <option value="" disabled data-id="element-396" data-mp="2uly44wwe0qmc">
                        Select your role
                      </option>
                      <option value="District leader/administrator" data-id="element-397" data-mp="2pnwbpld2ney7">
                        District leader/administrator
                      </option>
                      <option value="School leader (principal/AP)" data-id="element-398" data-mp="2zdfxays65mwr">
                        School leader (principal/AP)
                      </option>
                      <option value="Instructional coach" data-id="element-399" data-mp="2bqhh827yogzd">
                        Instructional coach
                      </option>
                      <option value="Teacher" data-id="element-400" data-mp="2kotiqzad8z6l">Teacher</option>
                      <option value="Partner organization" data-id="element-401" data-mp="2kmytj8s2akwp">
                        Partner organization
                      </option>
                      <option value="Other" data-id="element-402" data-mp="3w3ylg3ahyxq7">Other</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-500" data-id="element-403" data-mp="3s2n22fi7v8qg">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-404" data-mp="3g8hpb85u5do">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-405" data-mp="8g4otjc5mcxg" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5" data-id="element-406" data-mp="3uo4ayqc0iczp">
                  <label htmlFor="organization" className="block text-sm font-medium text-gray-700" data-id="element-407" data-mp="3uow6y29iucqi">
                    Organization or district name
                  </label>
                  <input type="text" id="organization" name="organization" required value={formData.organization} onChange={handleChange} onBlur={handleBlur} className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all text-gray-900 placeholder:text-gray-400" data-id="element-408" data-mp="r5t1txg1v8oj" />
                </div>

                <div className="space-y-1.5" data-id="element-409" data-mp="19xlxy0qf1a0h">
                  <div className="flex justify-between items-baseline" data-id="element-410" data-mp="1jsu6h1c9s8c6">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-411" data-mp="1js17imjjyn4l">
                      How many instructional coaches are on your team?
                    </label>
                    <span className="text-xs text-gray-500" data-id="element-412" data-mp="1aqtfunbjkkjh">Optional</span>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1" data-id="element-413" data-mp="15s9ryhde0yoh">
                    {['Just me', '2–4', '5–10', '11–25', '25+'].map(option => <label key={option} className={`inline-flex items-center px-4 py-2 rounded-lg border cursor-pointer transition-all text-sm ${formData.coachCount === option ? 'border-primary-500 bg-primary-50 text-primary-700 ring-2 ring-primary-500/20' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50'}`} data-id="element-414" data-mp="10w1f5amnxhkh">
                          <input type="radio" name="coachCount" value={option} checked={formData.coachCount === option} onChange={handleChange} className="sr-only" data-id="element-415" data-mp="i40atsr4cz3o" />
                          {option}
                        </label>)}
                  </div>
                </div>

                <div className="space-y-1.5" data-id="element-416" data-mp="2tqtx8z7y0qhc">
                  <div className="flex justify-between items-baseline" data-id="element-417" data-mp="2tpivf4y25ssv">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-418" data-mp="2ypdpfhd4qyi5">
                      How many teachers do you support?
                    </label>
                    <span className="text-xs text-gray-500" data-id="element-419" data-mp="32p5zjyi69cpm">Optional</span>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1" data-id="element-420" data-mp="25zes6yos7v2e">
                    {['1–20', '21–50', '51–100', '101–200', '200+'].map(option => <label key={option} className={`inline-flex items-center px-4 py-2 rounded-lg border cursor-pointer transition-all text-sm ${formData.teacherCount === option ? 'border-primary-500 bg-primary-50 text-primary-700 ring-2 ring-primary-500/20' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50'}`} data-id="element-421" data-mp="2f266d691l4xq">
                          <input type="radio" name="teacherCount" value={option} checked={formData.teacherCount === option} onChange={handleChange} className="sr-only" data-id="element-422" data-mp="1rasbotnf4x9l" />
                          {option}
                        </label>)}
                  </div>
                </div>

                <div className="space-y-1.5" data-id="element-423" data-mp="1hyl5dq4e9uyd">
                  <div className="flex justify-between items-baseline" data-id="element-424" data-mp="1hzcv4kbb8ygq">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-425" data-mp="1d5ic7j3j4nip">
                      What are you hoping to explore with Nisa?
                    </label>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1" data-id="element-426" data-mp="1lyljf9nl044w">
                    {['Improve coaching efficiency', 'Improve teacher outcomes', 'Increase system alignment', 'Explore a pilot', 'Learn more'].map(option => <label key={option} className={`inline-flex items-center px-4 py-2 rounded-lg border cursor-pointer transition-all text-sm ${formData.intent.includes(option) ? 'border-primary-500 bg-primary-50 text-primary-700 ring-2 ring-primary-500/20' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50'}`} data-id="element-427" data-mp="1vxubty2stap5">
                        <input type="checkbox" name="intent" value={option} checked={formData.intent.includes(option)} onChange={() => handleIntentToggle(option)} className="sr-only" data-id="element-428" data-mp="2i5gjww1s16ce" />
                        {option}
                      </label>)}
                  </div>
                </div>

                <div className="pt-4 space-y-4" data-id="element-429" data-mp="1ug5j5ekffby9">
                  {submitError && <p className="text-sm text-red-600 font-medium" role="alert" data-id="element-430" data-mp="1ueud183ids3z">
                      {submitError}
                    </p>}
                  <Button type="submit" size="lg" fullWidth disabled={isSubmitting} className="w-full sm:w-auto" data-id="element-431" data-mp="1ki7votae5j6p">
                    {isSubmitting ? <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-432" data-mp="1phays86t9fc1" />
                        Sending...
                      </> : <>
                        Request a Nisa demo
                        <ArrowRight className="w-5 h-5 ml-2" data-id="element-433" data-mp="3jujzpblajsio" />
                      </>}
                  </Button>
                  <p className="text-sm text-gray-500 text-center sm:text-left" data-id="element-434" data-mp="3if7wrrex58fe"></p>
                </div>
              </form>
            </>}
        </div>
      </div>
    </div>;
};